import React from 'react';
import ReactDOMServer from 'react-dom/server';

// react-dates needs to be initialized before using any react-dates component
// https://github.com/airbnb/react-dates#initialize
// NOTE: Initializing it here will initialize it also for app.test.js
import 'react-dates/initialize';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { SdkProvider } from './sdkContext'; // importujesz to co stworzyliśmy wyżej
import { Provider } from 'react-redux';
import difference from 'lodash/difference';
import mapValues from 'lodash/mapValues';
import moment from 'moment';
import { IntlProvider, RawIntlProvider, createIntl, createIntlCache } from 'react-intl';
import configureStore from './store';
import routeConfiguration from './routeConfiguration';
import Routes from './Routes';
import config from './config';

// Flex template application uses English translations as default.
import defaultMessages from './translations/en.json';
// import 'moment/locale/${process.env.REACT_APP_LANG}';
import 'moment/locale/pl';
// require(`moment/locale/${process.env.REACT_APP_LANG || 'pl'}`);
import 'moment/locale/hu';

import 'moment/locale/lt';

import 'moment/locale/cs';

import 'moment/locale/lv';

import 'moment/locale/et';

import 'moment/locale/sk';
import { error } from './util/log';
// const localeMapping = {
//   pl: 'pl',
//   en: 'en-gb',
//   hu: 'hu',
//   lt: 'lt',
//   lv: 'lv',
//   cz: 'cs'
// };
// console.log(localeMapping[process.env.REACT_APP_LANG])
// require(`moment/locale/${localeMapping[process.env.REACT_APP_LANG]}`);

// Step 3:
// If you are using a non-english locale, point `messagesInLocale` to correct .json file
// import messagesInLocale from './translations/pl.json';
const messagesInLocale = require(`./translations/${process.env.REACT_APP_LANG || 'pl'}.json`)
// const messagesInB = require(`./translations/${process.env.REACT_APP_LANG}.json`);
// console.log(messagesInB)

// If translation key is missing from `messagesInLocale` (e.g. fr.json),
// corresponding key will be added to messages from `defaultMessages` (en.json)
// to prevent missing translation key errors.
const addMissingTranslations = (sourceLangTranslations, targetLangTranslations) => {
  const sourceKeys = Object.keys(sourceLangTranslations);
  const targetKeys = Object.keys(targetLangTranslations);
  const missingKeys = difference(sourceKeys, targetKeys);

  const addMissingTranslation = (translations, missingKey) => ({
    ...translations,
    [missingKey]: sourceLangTranslations[missingKey],
  });

  return missingKeys.reduce(addMissingTranslation, targetLangTranslations);
};

const isDefaultLanguageInUse = config.locale === 'en';

const messages = isDefaultLanguageInUse
  ? defaultMessages
  : addMissingTranslations(defaultMessages, messagesInLocale);

const isTestEnv = process.env.NODE_ENV === 'test';

// Locale should not affect the tests. We ensure this by providing
// messages with the key as the value of each message.
const testMessages = mapValues(messages, (val, key) => key);
const localeMessages = isTestEnv ? testMessages : messages;

const setupLocale = () => {
  if (isTestEnv) {
    // Use english as a default locale in tests
    // This affects app.test.js and app.node.test.js tests
    config.locale = 'en';
    return;
  }

  // exception for Czech locale
  const czechConfig = [
    'cs',
    {
      months: {
        format: 'leden_únor_březen_duben_květen_červen_červenec_srpen_září_říjen_listopad_prosinec'.split('_'),
        standalone: 'leden_únor_březen_duben_květen_červen_červenec_srpen_září_říjen_listopad_prosinec'.split('_')
      },
    }
  ]

  // Set the Moment locale globally
  // See: http://momentjs.com/docs/#/i18n/changing-locale/
  if (config.locale === 'cz') {
    moment.locale(...czechConfig);
  } else {
    moment.locale(config.locale);
  }

};

const localeValue = config.langIdForCountryId.find(({ countryId }) => countryId === config.locale)?.langId;

const cache = createIntlCache()

const intl = createIntl({
  locale: localeValue,
  messages: localeMessages,
  textComponent: "span",
  onError: (err) => {
    return
  }
}, cache)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const ClientApp = props => {
  const { store, sdk } = props;
  setupLocale();

  return (
    <RawIntlProvider value={intl}>
      <QueryClientProvider client={queryClient}>
        <SdkProvider sdk={sdk}>
          <Provider store={store}>
            <HelmetProvider>
              <BrowserRouter>
                <Routes routes={routeConfiguration()} />
              </BrowserRouter>
            </HelmetProvider>
          </Provider>
        </SdkProvider>
      </QueryClientProvider>
    </RawIntlProvider>
  );
};

export const ServerApp = props => {
  const { url, context, helmetContext, store } = props;
  setupLocale();
  HelmetProvider.canUseDOM = false;
  return (
    <IntlProvider locale={config.locale} messages={localeMessages} textComponent="span">
      <Provider store={store}>
        <HelmetProvider context={helmetContext}>
          <StaticRouter location={url} context={context}>
            <Routes routes={routeConfiguration()} />
          </StaticRouter>
        </HelmetProvider>
      </Provider>
    </IntlProvider>
  );
};

/**
 * Render the given route.
 *
 * @param {String} url Path to render
 * @param {Object} serverContext Server rendering context from react-router
 *
 * @returns {Object} Object with keys:
 *  - {String} body: Rendered application body of the given route
 *  - {Object} head: Application head metadata from react-helmet
 */
export const renderApp = (url, serverContext, preloadedState) => {
  // Don't pass an SDK instance since we're only rendering the
  // component tree with the preloaded store state and components
  // shouldn't do any SDK calls in the (server) rendering lifecycle.
  const store = configureStore(preloadedState);

  const helmetContext = {};

  const body = ReactDOMServer.renderToString(
    <ServerApp url={url} context={serverContext} helmetContext={helmetContext} store={store} />
  );
  const { helmet: head } = helmetContext;
  return { head, body };
};
